import React, { useCallback, useEffect, useState } from 'react';

import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { ComponentLifecycleCallbacks } from 'src/components/generic/ComponentLifecycleCallbacks';
import type { AlineClientEditor } from 'src/layout-engine';
import { useEditorOnChangeCounter } from 'src/layout-engine';

import { useRequiredEditorRuntime } from './editor-runtime-registry';
import { createDocumentIgnoredLintsStore } from './spellcheck-ignored-lints';
import { SpellcheckSession } from './spellcheck-session';
import { useSpellcheckStateSelector, useSpellcheckTabId } from './spellcheck-state-registry';
import { useSpellcheckAvailable } from './use-spellcheck-available';
import { useSpellcheckEnabled } from './use-spellcheck-enabled';

/**
 * Headless spellcheck detector. Must live inside `<Slate>` for `useEditorOnChangeCounter` to fire on
 * edits, which the drawer cannot do from outside the editor tree.
 *
 * Keyed by the document it belongs to. The outline swaps documents without unmounting the editor,
 * and a session kept across that swap would go on reading and writing the dismissals of whichever
 * document happened to be opened first.
 */
export function SpellcheckRunner() {
  const { documentId, editor } = useRequiredEditorRuntime();
  const tabId = useSpellcheckTabId();
  const client = useApolloClient();

  return (
    <SpellcheckDocumentRunner
      key={documentId}
      client={client}
      documentId={documentId}
      editor={editor}
      tabId={tabId}
    />
  );
}

/** One document's spellchecker. Rebuilt from scratch whenever the document changes. */
function SpellcheckDocumentRunner({
  client,
  documentId,
  editor,
  tabId,
}: {
  client: ApolloClient;
  documentId: UUID;
  editor: AlineClientEditor;
  tabId: UUID;
}): JSX.Element {
  const onChangeCounter = useEditorOnChangeCounter();
  const { enabled: userEnabled, loading: documentLoading } = useSpellcheckEnabled();
  const available = useSpellcheckAvailable();
  const enabled = userEnabled && available && !documentLoading;
  const ignoreRequests = useSpellcheckStateSelector(state => state.ignoreRequests);
  const accepted = useSpellcheckStateSelector(state => state.accepted);
  const [session] = useState(
    () =>
      new SpellcheckSession({
        documentId,
        editor,
        ignoredLintsStore: createDocumentIgnoredLintsStore(client),
        tabId,
      })
  );

  // Before `connect`, so a pane shown again reconnects already knowing whether spellcheck is on.
  useEffect(() => session.setEnabled(enabled), [enabled, session]);

  // A hidden tab pane is not unmounted, so this disconnects and reconnects on every tab switch.
  useEffect(() => session.connect(), [session]);

  useEffect(() => session.scheduleLint(), [onChangeCounter, session]);

  useEffect(() => session.flushIgnoreRequests(), [ignoreRequests, session]);

  useEffect(() => session.handleAccepted(), [accepted, session]);

  // Effect cleanup runs on every tab switch, so only a real unmount may release the worker and refs.
  const dispose = useCallback(() => session.dispose(), [session]);

  return <ComponentLifecycleCallbacks onUnmount={dispose} />;
}
